import {
  AcademicCapIcon,
  ArrowsRightLeftIcon,
  BellIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  CubeIcon,
  HomeIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
  TagIcon,
  UserCircleIcon,
} from '@heroicons/react/24/solid';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { isClassicActive } from '../../helpers/helpers';
import { useAuth } from '../../zustand/Zustand';

type NavGameVersion = 'retail' | 'classic-era';

interface NavTool {
  path: string;
  icon: typeof Squares2X2Icon;
  labelKey: string;
  // Game versions this tool applies to. Omitted = shown for every version.
  versions?: NavGameVersion[];
}

const NAV_TOOLS: NavTool[] = [
  { path: '/dashboard', icon: Squares2X2Icon, labelKey: 'toolItems.dashboard' },
  { path: '/tools/flipping', icon: ArrowsRightLeftIcon, labelKey: 'toolItems.flippingTool', versions: ['retail'] },
  { path: '/tools/dropship', icon: CubeIcon, labelKey: 'toolItems.dropshipTool', versions: ['retail'] },
  { path: '/tools/housing', icon: HomeIcon, labelKey: 'toolItems.housingTool', versions: ['retail'] },
  { path: '/tools/inventory', icon: MagnifyingGlassIcon, labelKey: 'toolItems.inventoryAnalysisTool', versions: ['retail'] },
  { path: '/tools/classic-deals', icon: TagIcon, labelKey: 'toolItems.classicDeals', versions: ['classic-era'] },
  { path: '/tools/snipe', icon: BellIcon, labelKey: 'toolItems.snipeAlerts' },
  { path: '/tools/stats', icon: ChartBarIcon, labelKey: 'toolItems.realmStats', versions: ['retail'] },
  { path: '/tools/tsm', icon: ClipboardDocumentListIcon, labelKey: 'toolItems.tsmListGenerator', versions: ['retail'] },
  { path: '/guides/tsm-profile', icon: AcademicCapIcon, labelKey: 'toolItems.tsmProfileSetupGuide', versions: ['retail'] },
];

const NAV_UTILITY = [
  { path: '/subscription', icon: CreditCardIcon, labelKey: 'nav.subscription' },
  { path: '/settings/realm', icon: Cog6ToothIcon, labelKey: 'nav.configuration' },
  { path: '/account', icon: UserCircleIcon, labelKey: 'nav.account' },
] as const;

const NavRailButton: React.FC<{
  title: string;
  isActive: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ title, isActive, onClick, children }) => (
  <div className="relative group shrink-0">
    <button
      aria-label={title}
      aria-current={isActive ? 'page' : undefined}
      onClick={onClick}
      className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border-0 cursor-pointer transition-all duration-120 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
        isActive
          ? 'bg-blue-500/18 shadow-[inset_0_0_0_1px_rgba(59,130,246,.3)] text-blue-300'
          : 'bg-transparent text-slate-500 hover:bg-white/6 hover:text-slate-400'
      }`}
    >
      {children}
    </button>
    {/* Instant styled tooltip — shown on hover and keyboard focus */}
    <span
      role="tooltip"
      className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 z-50 whitespace-nowrap rounded-md bg-fp-card border border-fp-border px-2 py-1 text-xs text-slate-200 shadow-lg opacity-0 transition-opacity duration-100 delay-100 group-hover:opacity-100 group-has-[:focus-visible]:opacity-100"
    >
      {title}
    </span>
  </div>
);

const NavRail: React.FC = () => {
  const { t } = useTranslation(['common']);
  const location = useLocation();
  const navigate = useNavigate();
  const { userData } = useAuth();
  const isAdmin = userData?.role === 'admin';
  // While classic support is feature-flagged off, always navigate as retail
  // even if the user document still carries a classic game version.
  const gameVersion: NavGameVersion = isClassicActive() ? (userData?.gameVersion ?? 'retail') : 'retail';
  const visibleTools = NAV_TOOLS.filter((tool) => !tool.versions || tool.versions.includes(gameVersion));

  return (
    <nav
      aria-label={t('common:nav.tools')}
      className="flex flex-col items-center py-2 shrink-0 w-13 bg-fp-header border-r border-fp-border"
    >
      {/* Tool icons */}
      <div className="flex flex-col items-center gap-0.5">
        {visibleTools.map(({ path, icon: Icon, labelKey }) => {
          const isActive = location.pathname.startsWith(path);
          return (
            <NavRailButton
              key={path}
              title={t(`common:${labelKey}`)}
              isActive={isActive}
              onClick={() => navigate(path)}
            >
              <Icon className="w-4.5 h-4.5" />
            </NavRailButton>
          );
        })}
      </div>

      {/* Divider */}
      <div className="w-8 h-px bg-slate-600 my-3 shrink-0" />

      {/* Utility icons */}
      <div className="flex flex-col items-center gap-0.5">
        {NAV_UTILITY.map(({ path, icon: Icon, labelKey }) => {
          const isActive = location.pathname.startsWith(path);
          return (
            <NavRailButton
              key={path}
              title={t(`common:${labelKey}`)}
              isActive={isActive}
              onClick={() => navigate(path)}
            >
              <Icon className="w-4.5 h-4.5" />
            </NavRailButton>
          );
        })}

        {isAdmin && (
          <NavRailButton
            title={t('common:nav.administration')}
            isActive={location.pathname.startsWith('/admin')}
            onClick={() => navigate('/admin/stats')}
          >
            <ShieldCheckIcon className="w-4.5 h-4.5" />
          </NavRailButton>
        )}
      </div>
    </nav>
  );
};

export default NavRail;
